import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
//import { useTranslation } from 'react-i18next';
import { Tooltip, Typography } from '@material-ui/core';
import { useProperties } from 'hooks';
import SimpleTable from 'components/table/SimpleTable';
import { DashboardHelpers } from 'utils';
import _ from 'lodash';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MobileTable from 'components/table/MobileTable';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    width: '100%',
    height: '100%',
    minHeight: '100%',
    flex: 1,
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  icon: {
    color: theme.palette.icon,
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  toggleButtonGroup: {
    maxHeight: '35px',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      width: '100%',
    },
  },
  toggleButton: {
    minWidth: 100,
    [theme.breakpoints.down('sm')]: {
      minWidth: '33%',
    },
  },
  toggleLabel: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  cardHeader: {
    padding: '0',
    zIndex: 200,
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  picker: {
    marginTop: theme.spacing(1),
  },
  toggles: {
    display: 'flex',
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    height: 42,
    width: 42,
    marginRight: theme.spacing(1),
  },
  actions: {
    padding: theme.spacing(1),
    justifyContent: 'flex-end',
  },
  visibleSmall: {
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
}));

const DeviceListTable = ({
  title,
  devices,
  onDeviceCheckboxClick,
  selectedDevicesIds,
  selectSingleDevice,
  searchText,
}) => {
  const classes = useStyles();
  const mobileView = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const { t } = useTranslation();
  const tableHeader = [
    {
      alias: 'hw_address',
      sortable: true,
      mobilePreview: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'hw_address',
      dataRender: (s) => {
        if (s.hw_address != undefined && s.hw_address.length > 8) {
          return (
            <Typography className={classes.nameCell} align={'center'}>
              {DashboardHelpers.trimHWAddress(s.hw_address)}
            </Typography>
          );
        }
      },
    },
    {
      alias: 'name',
      sortable: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'name',
      className: 'hiddenXSmall',
      dataRender: (s) => (
        <Typography className={classes.nameCell} align={'center'}>
          {s.name}
        </Typography>
      ),
    },
    {
      alias: 'field',
      sortable: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'field_name',
      dataRender: (s) => (
        <Typography className={classes.nameCell} variant="subtitle1" align="center">
          {s.field_name}
        </Typography>
      ),
    },
    {
      alias: 'last_seen',
      sortable: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'updated_at',
      dataRender: (s) => {
        let lastSeen = t('never');
        if (s.attributes && s.attributes.last_seen) {
          lastSeen = DashboardHelpers.formatDateTime(s.attributes.last_seen);
        }

        return (
          <Typography className={classes.nameCell} variant="subtitle1" align="center">
            {lastSeen}
          </Typography>
        );
      },
    },
    {
      alias: 'gateway_id',
      sortable: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'gateway_id',
      dataRender: (s) => {
        if (s.gateway_id) {
          return (
            <Typography className={classes.nameCell} variant="subtitle1" align="center">
              {s.gateway_id}
            </Typography>
          );
        }
      },
    },
    {
      alias: 'gw_last_seen',
      sortable: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'gw_last_seen',
      dataRender: (s) => {
        if (s.gw_last_seen && s.gw_last_seen !== '0001-01-01T00:00:00Z') {
          return (
            <Typography className={classes.nameCell} variant="subtitle1" align="center">
              {DashboardHelpers.formatDateTime(s.gw_last_seen)}
            </Typography>
          );
        }
      },
    },
    {
      alias: 'rssi',
      sortable: false,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'rssi',
      dataRender: (s) => {
        if (s.rssi !== undefined) {
          return (
            <Tooltip title={s.rssi} placement="top-start">
              {DashboardHelpers.getIconForRSSIValue(s.rssi)}
            </Tooltip>
          );
        } else {
          return (
            <Typography className={classes.nameCell} variant="subtitle1" align="center">
              {t('no_signal')}
            </Typography>
          );
        }
      },
    },
  ];

  const { isFetchingProperties } = useProperties();
  let selectAllChecked = false;
  const devicesIds = _.map(devices, 'id');

  let searchedDevices = _.filter(devices, (device) => {
    return DashboardHelpers.searchObject(device, searchText, tableHeader);
  });

  if (_.difference(devicesIds, selectedDevicesIds).length === 0 && devices.length !== 0) {
    selectAllChecked = true;
  } else {
    selectAllChecked = false;
  }

  const handleSelectAll = (event, selectAllBool) => {
    onDeviceCheckboxClick(devices, selectAllBool);
    selectAllChecked = selectAllBool;
  };

  return mobileView ? (
    <MobileTable title={title} tableHeader={tableHeader} data={searchedDevices} selectRow={selectSingleDevice} />
  ) : (
    <SimpleTable
      title={title}
      tableHeader={tableHeader}
      data={searchedDevices}
      isLoading={isFetchingProperties}
      selected={selectedDevicesIds}
      selectRow={selectSingleDevice}
      showCheckbox
      selectCheckbox={onDeviceCheckboxClick}
      selectAll={handleSelectAll}
      selectAllChecked={selectAllChecked}
    />
  );
};

DeviceListTable.propTypes = {
  title: PropTypes.string,
  devices: PropTypes.array,
  onDeviceCheckboxClick: PropTypes.func.isRequired,
  selectedDevicesIds: PropTypes.array,
  selectSingleDevice: PropTypes.func.isRequired,
  searchText: PropTypes.string,
};

DeviceListTable.defaultProps = {
  title: '',
  fields: {},
  devices: [],
  selectedDevicesIds: [],
  searchText: '',
};

export default DeviceListTable;
